import styles from "./report-list.module.less";
import Accordion, { TAccordionItem } from "../accordion/accordion";
import { useTranslation } from "react-i18next";

export type TReportItem = {
  url?: string;
  name?: string;
  date?: string;
  reportType: string;
  investorCallDate?: string;
};

export type TReportingYear = {
  year: string;
  isOpen?: boolean;
  reportItems: Array<TReportItem>;
};

export type TReportListProps = {
  reportingYears: Array<TReportingYear>;
  disableInvestorCallDate?: boolean;
};

const generateRerportName = (report: TReportItem, year: string) => {
  const { t } = useTranslation();

  switch (report.reportType.toLowerCase()) {
    case "q1":
    case "q2":
    case "q3":
    case "q4":
      return `${t("REPORT_LIST_INTERIM_REPORT")} ${report.reportType.toUpperCase()} ${year}`;
    case "annual":
      return `${t("REPORT_LIST_ANNUAL_REPORT")} ${year}`;
    case "sustainability":
      return `${t("REPORT_LIST_SUSTAINABILITY_REPORT")} ${year}`;
    case "other":
    default:
      return `${report.reportType} ${t("REPORT_LIST_REPORT")} ${year}`;
  }
};

type TReportListRow = {
  report: TReportItem;
  year: string;
  disableInvestorCallDate: boolean;
};

const ReportListRow = ({ report, year, disableInvestorCallDate }: TReportListRow) => {
  const { t } = useTranslation();

  const reportName = report.name ? report.name : generateRerportName(report, year);

  return (
    <div className={styles.listRowWrapper}>
      <div className={styles.reportListDateExplanationWrapperMobile}>
        {!disableInvestorCallDate && (
          <p className={[styles.reportListDateExplanationTextMobile, "body-base"].join(" ")}>
            {t("REPORT_LIST_INVESTOR_CALL")}
          </p>
        )}
        <p className={[styles.reportListDateExplanationTextMobile, "body-base"].join(" ")}>
          {t("REPORT_LIST_PUBLISHED_DATE")}
        </p>
      </div>
      {report.url ? (
        <a className={styles.itemLink} href={report.url} target={"_blank"}>
          <p className={styles.itemLinkText}>{reportName}</p>
          <span className={[styles.itemLinkIcon, "ui-icon-download"].join(" ")} />
        </a>
      ) : (
        <div className={styles.itemLink}>
          <p className={[styles.itemLinkText, styles.disabled].join(" ")}>{reportName}</p>
        </div>
      )}

      <div
        className={[
          styles.datesWrapper,
          disableInvestorCallDate ? styles.singleDate : "",
          report.url ? "" : styles.disabled,
        ].join(" ")}
      >
        {!disableInvestorCallDate && <span className={styles.investorCallDate}>{report.investorCallDate ?? ""}</span>}
        <span className={styles.publishedDate}>{report.date ?? ""}</span>
      </div>
    </div>
  );
};

const getReportsPreparedForAccordion = (
  reportingYears: Array<TReportingYear>,
  disableInvestorCallDate: boolean
): Array<TAccordionItem> => {
  if (!reportingYears) {
    return [];
  }

  const accordionItems: Array<TAccordionItem> = reportingYears
    .sort((yearA, yearB) => (Number(yearA.year) > Number(yearB.year) ? -1 : 1))
    .map((reportingYear) => {
      const { t } = useTranslation();

      const interrimReports: Array<TReportItem> = [];
      const annualReports: Array<TReportItem> = [];

      reportingYear.reportItems.forEach((report) => {
        if (
          report.reportType.toLowerCase() === "annual" ||
          report.reportType.toLowerCase() === "sustainability" ||
          report.reportType.toLowerCase() === "other"
        ) {
          annualReports.push(report);
        } else {
          interrimReports.push(report);
        }
      });

      // Commented this out due to Martin want to test which way of sorting is the most relevant (he sorts manually in the CMS)
      // interrimReports.sort((reportA, reportB) => (reportA.reportType > reportB.reportType ? 1 : -1));

      const content = (
        <div className={styles.yearWrapper}>
          <div className={styles.reportListDateExplanationWrapper}>
            <p className={[styles.reportListDateExplanationText, "body-base bold"].join(" ")}>
              {!disableInvestorCallDate && <span>{t("REPORT_LIST_INVESTOR_CALL")} / </span>}
              <span>{t("REPORT_LIST_PUBLISHED_DATE")}</span>
            </p>
          </div>
          <ul className={styles.list}>
            {interrimReports.map((report) => {
              return (
                <li key={report.url} className={styles.item}>
                  <p className={[styles.reportTypeHeading, "body-base bold"].join(" ")}>
                    {report.reportType.toUpperCase()}
                  </p>
                  <ReportListRow
                    report={report}
                    year={reportingYear.year}
                    disableInvestorCallDate={disableInvestorCallDate}
                  />
                </li>
              );
            })}
            {Array.isArray(annualReports) && annualReports.length > 0 && (
              <li className={styles.item}>
                <p className={[styles.reportTypeHeading, "body-base bold"].join(" ")}>
                  {t("REPORT_LIST_ANNUAL_REPORTS")}
                </p>
                {annualReports.map((report) => {
                  return (
                    <ReportListRow
                      report={report}
                      year={reportingYear.year}
                      disableInvestorCallDate={disableInvestorCallDate}
                    />
                  );
                })}
              </li>
            )}
          </ul>
        </div>
      );

      return {
        id: reportingYear.year,
        heading: reportingYear.year,
        content: content,
        open: reportingYear.isOpen ?? false,
      };
    });

  return accordionItems;
};

const ReportList = ({ reportingYears, disableInvestorCallDate }: TReportListProps) => {
  const accordionItems = getReportsPreparedForAccordion(reportingYears, disableInvestorCallDate ? true : false);

  return (
    <div className={styles.wrapper}>
      <div className={[styles.contentWrapper].join(" ")}>
        <div className={"heading-max-width"}>
          <Accordion items={accordionItems} />
        </div>
      </div>
    </div>
  );
};

export default ReportList;
